import React, { useRef, useState } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { Button } from "@shared/ui/button";
import { ArrowLeft, Save, User, FileText, Clock } from "lucide-react";
import { useToast } from "@shared/hooks/use-toast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@shared/ui/alert-dialog";
import { Tabs } from "@shared/ui/tabs";
import { TooltipProvider } from "@shared/ui/tooltip";
import { EditBasicInfoTab } from "./tabs/basicInfo";
import { EditOtherDetailsTab } from "./tabs/otherDetails";
import { EditAvailabilityTab } from "./tabs/availability";
import { useGetFieldForceDetails } from "@features/wx/fieldForce/hooks";

// Define the PersonData type to match our data structure
type PersonData = {
  id: string;
  name: string;
  role: string;
  zone: string;
  area: string;
  contact: string;
  joiningDate: string;
  status: string;
  email?: string;
  imei?: string;
  address?: string;
};

type PreSelectedData = {
  department: string;
  role: string;
};

// Add form data state types
type BasicFormData = {
  fid: string;
  fieldForceNo: string;
  name: string;
  role: string;
  department: string;
  email: string;
  phone: string;
  imei: string;
  joinDate: string;
  address: string;
  password: string;
  confirmPassword: string;
  documentUpload?: File | null;
};

type OtherDetailsFormData = {
  skills: string[];
  area: string[];
  subArea: string[];
  normalPay: string;
  overtimePay: string;
  certificateName: string[];
};

type AvailabilityFormData = {
  weeklySchedule: {
    monday: DaySchedule;
    tuesday: DaySchedule;
    wednesday: DaySchedule;
    thursday: DaySchedule;
    friday: DaySchedule;
    saturday: DaySchedule;
    sunday: DaySchedule;
  };
};

type DaySchedule = {
  available: boolean;
  startTime: string;
  endTime: string;
  availableOvertime: boolean;
};

const fieldForceEdit = () => {
  const { id, ff_no } = useParams<{ id: string; ff_no: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const initialPersonData = location.state?.personData || null;
  const preSelected = location.state?.preSelected as
    | PreSelectedData
    | undefined;
  const { data: getFieldForceDetails } = useGetFieldForceDetails(ff_no);

  const [showReturnDialog, setShowReturnDialog] = useState(false);

  // ADD: Form data states for each tab
  const [basicFormData, setBasicFormData] = useState<BasicFormData | null>(
    null
  );
  const [otherDetailsFormData, setOtherDetailsFormData] =
    useState<OtherDetailsFormData | null>(null);
  const [availabilityFormData, setAvailabilityFormData] =
    useState<AvailabilityFormData | null>(null);

  const handleReturn = (stayOnSetup: boolean) => {
    if (stayOnSetup) {
      setShowReturnDialog(false);
    } else {
      navigate("/field-force");
    }
  };

  // Tab components configuration with state management
  const tabComponents = {
    basic: {
      label: "Basic Info",
      shortLabel: "Basic",
      icon: <User size={16} />,
      component: (
        <EditBasicInfoTab
          preSelected={preSelected}
          initialPersonData={getFieldForceDetails}
        />
      ),
    },
    other: {
      label: "Other Details",
      shortLabel: "Details",
      icon: <FileText size={16} />,
      component: (
        <EditOtherDetailsTab
          initialPersonData={getFieldForceDetails}
          currentFormData={otherDetailsFormData}
          onFormDataChange={setOtherDetailsFormData}
        />
      ),
    },
    availability: {
      label: "Availability",
      shortLabel: "Schedule",
      icon: <Clock size={16} />,
      component: (
        <EditAvailabilityTab
          initialPersonData={getFieldForceDetails}
          currentFormData={availabilityFormData}
          onFormDataChange={setAvailabilityFormData}
        />
      ),
    },
  };

  const urlMapping = {
    basic: "basic-info",
    other: "other-details",
    availability: "availability",
  };

  return (
    <TooltipProvider>
      <div className="space-y-6 animate-fade-in">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              onClick={() => navigate("/service-orders/field-force/")}
            >
              <ArrowLeft size={16} />
            </Button>
            <div>
              <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
                Edit Technician
                {preSelected && (
                  <span className="text-base font-normal text-muted-foreground">
                    – managed under Utility Setup
                  </span>
                )}
              </h1>
              {preSelected && (
                <p className="text-sm text-muted-foreground">
                  Technicians are part of your Utility's Operations team. Please
                  complete their profile here.
                </p>
              )}
            </div>
          </div>
        </div>

        <Tabs
          defaultValue="basic"
          urlMapping={urlMapping}
          tabComponents={tabComponents}
          tabsListClassName="grid w-full grid-cols-3 mb-6"
          idPrefix="technician-tab"
          level={0}
        />

        {/* Return Dialog */}
        <AlertDialog open={showReturnDialog} onOpenChange={setShowReturnDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                Technician Edited Successfully
              </AlertDialogTitle>
              <AlertDialogDescription>
                You've successfully edited the technician in your field force.
                Where would you like to go now?
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => handleReturn(true)}>
                Stay on Utility Setup
              </AlertDialogCancel>
              <AlertDialogAction onClick={() => handleReturn(false)}>
                Take me back to Service Orders
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </TooltipProvider>
  );
};

export default fieldForceEdit;
